import React, { useState, useRef, useEffect } from 'react';
import { Download, Plus, X } from 'lucide-react';
import { AuthProvider, useAuth } from './components/Auth/AuthProvider';
import { getOrCreateGarden, updateGarden, getWords, addWord as addWordToDb, deleteWord, rearrangeWords } from './lib/api';
import { FLOWER_COLORS } from './config/flowers';

function WordBloomApp() {
  const { user } = useAuth();
  
  const [garden, setGarden] = useState(null);
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWord, setNewWord] = useState('');
  const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0]);
  const [bgIndex, setBgIndex] = useState(1);
  const [babyName, setBabyName] = useState('');
  const [babyBirthdate, setBabyBirthdate] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [insectSeed, setInsectSeed] = useState(0);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const gardenRef = useRef(null);
  
  const bgOptions = [
    { name: 'White', color: '#FFFFFF' },
    { name: 'Cream', color: '#F5E6D3' },
    { name: 'Black', color: '#1A1A1A' }
  ];

  useEffect(() => {
    async function loadGardenData() {
      try {
        if (user) {
          // User is logged in - load from Supabase
          const gardenData = await getOrCreateGarden(user.id);
          setGarden(gardenData);
          
          // Set baby info from garden
          setBabyName(gardenData.baby_name || '');
          setBabyBirthdate(gardenData.baby_birthdate || '');
          
          // Load words from database
          const wordsData = await getWords(gardenData.id);
          
          // Convert database words to app format
          const formattedWords = wordsData.map(w => ({
            id: w.id,
            text: w.text,
            date: w.date,
            order: w.word_order,
            cs: FLOWER_COLORS[w.flower_color_index],
            rot: w.rotation,
            sc: w.scale
          }));
          
          setWords(formattedWords);
          
          // Check if there's localStorage data to migrate
          const localWords = localStorage.getItem('wordBloom');
          if (localWords && formattedWords.length === 0) {
            // Migrate localStorage to database
            await migrateLocalStorageToDb(gardenData.id, localWords);
          }
        } else {
          // Not logged in - load from localStorage
          const saved = localStorage.getItem('wordBloom');
          if (saved) {
            try { setWords(JSON.parse(saved)); } catch (e) {}
          }
          const savedName = localStorage.getItem('wordBloomBabyName');
          const savedBirthdate = localStorage.getItem('wordBloomBabyBirthdate');
          if (savedName) setBabyName(savedName);
          if (savedBirthdate) setBabyBirthdate(savedBirthdate);
        }
      } catch (error) {
        console.error('Error loading garden:', error);
      } finally {
        setLoading(false);
      }
    }
    
    loadGardenData();
  }, [user]);
  
  async function migrateLocalStorageToDb(gardenId, localWordsJson) {
    try {
      const localWords = JSON.parse(localWordsJson);
      
      // Add each word to database
      for (const word of localWords) {
        await addWordToDb(
          gardenId,
          word.text,
          word.date,
          word.order
        );
      }
      
      // Reload from database to get the new data
      const wordsData = await getWords(gardenId);
      const formattedWords = wordsData.map(w => ({
        id: w.id,
        text: w.text,
        date: w.date,
        order: w.word_order,
        cs: FLOWER_COLORS[w.flower_color_index],
        rot: w.rotation,
        sc: w.scale
      }));
      
      setWords(formattedWords);
      
      // Clear localStorage since it's now in database
      localStorage.removeItem('wordBloom');
    } catch (error) {
      console.error('Error migrating data:', error);
    }
  }

  useEffect(() => {
    if (!user) {
      // Not logged in - save to localStorage
      if (words.length > 0) {
        localStorage.setItem('wordBloom', JSON.stringify(words));
      } else {
        localStorage.removeItem('wordBloom');
      }
    }
    // If logged in, words are already saved to DB when added/deleted
  }, [words, user]);

  useEffect(() => {
    if (!user) {
      localStorage.setItem('wordBloomBabyName', babyName);
    } else if (garden) {
      updateGarden(garden.id, { baby_name: babyName || null });
    }
  }, [babyName, user, garden]);

  useEffect(() => {
    if (!user) {
      localStorage.setItem('wordBloomBabyBirthdate', babyBirthdate);
    } else if (garden) {
      updateGarden(garden.id, { baby_birthdate: babyBirthdate || null });
    }
  }, [babyBirthdate, user, garden]);

  const flowerColors = [
    { p: ['#D4696B', '#E88B8D', '#F2A8AA'], c: '#8B3A3D', l: '#7A9B6F' },
    { p: ['#5B8FA3', '#7DACC0', '#9BC5D9'], c: '#2F4858', l: '#6B8E7D' },
    { p: ['#E8B864', '#F4D699', '#F9E5BB'], c: '#A67939', l: '#8DAA7F' },
    { p: ['#9BB89F', '#B5D4BA', '#D1E8D5'], c: '#5D7861', l: '#6F8B73' },
    { p: ['#B87B8E', '#D4A0B1', '#E8BFD0'], c: '#7A4555', l: '#7D9680' },
  ];
  
  const stemColor = '#6B7D5F';
  const currentBg = bgOptions[bgIndex].color;

  const addWord = async () => {
    if (!newWord.trim()) return;
    
    // Check if user needs to log in (10 word limit for guests)
    if (!user && words.length >= 10) {
      setShowAddForm(false);
      setShowLoginPrompt(true);
      return;
    }
    
    // Hard cap at 500 words (safety limit)
    if (words.length >= 500) {
      alert('Maximum 500 words reached! Contact hello@wordbloom.app if you need more space.');
      setShowAddForm(false);
      return;
    }
    
    const isFirst = words.length === 0;
    
    if (user && garden) {
      // Logged in - save to database
      try {
        const wordData = await addWordToDb(
          garden.id,
          newWord.trim(),
          newDate,
          words.length
        );
        
        // Add to local state
        setWords([...words, {
          id: wordData.id,
          text: wordData.text,
          date: wordData.date,
          order: wordData.word_order,
          cs: FLOWER_COLORS[wordData.flower_color_index],
          rot: wordData.rotation,
          sc: wordData.scale
        }]);
      } catch (error) {
        console.error('Error adding word:', error);
        alert('Failed to add word. Please try again.');
      }
    } else {
      // Not logged in - just add to local state (localStorage will save it)
      setWords([...words, {
        id: Date.now(),
        text: newWord.trim(),
        date: newDate,
        type: Math.floor(Math.random() * 4),
        cs: flowerColors[Math.floor(Math.random() * 5)],
        rot: (Math.random() - 0.5) * 25,
        sc: isFirst ? 2.2 : (1.3 + Math.random() * 0.5),
        order: words.length
      }]);
    }
    
    setNewWord('');
    setShowAddForm(false);
  };

  const rearrange = async () => {
    if (user && garden) {
      // Logged in - rearrange in database
      try {
        const rearrangedWords = await rearrangeWords(garden.id);
        
        // Update local state
        const formattedWords = rearrangedWords.map(w => ({
          id: w.id,
          text: w.text,
          date: w.date,
          order: w.word_order,
          cs: FLOWER_COLORS[w.flower_color_index],
          rot: w.rotation,
          sc: w.scale
        }));
        
        setWords(formattedWords);
      } catch (error) {
        console.error('Error rearranging:', error);
      }
    } else {
      // Not logged in - rearrange locally
      setWords(words.map(w => ({
        ...w,
        cs: flowerColors[Math.floor(Math.random() * 5)],
        rot: (Math.random() - 0.5) * 25,
        sc: w.order === 0 ? 2.2 : (1.3 + Math.random() * 0.5)
      })));
    }
    
    setInsectSeed(Math.random());
  };

  const getVB = () => {
    const n = words.length;
    let baseSize;
    if (n <= 1) baseSize = 400;
    else if (n <= 3) baseSize = 480;
    else if (n <= 8) baseSize = 600;
    else if (n <= 15) baseSize = 750;
    else if (n <= 25) baseSize = 950;
    else if (n <= 40) baseSize = 1100;
    else baseSize = 1280;
    
    if (babyName || babyBirthdate) {
      baseSize += 80;
    }
    
    return baseSize;
  };

  const getScale = () => {
    const n = words.length;
    if (n <= 1) return 1.8;
    if (n <= 3) return 1.5;
    if (n <= 8) return 1.2;
    if (n <= 15) return 1.0;
    if (n <= 25) return 0.85;
    if (n <= 40) return 0.75;
    return 0.65;
  };

  const getPos = (i) => {
    const s = getVB();
    const c = s / 2;
    
    const hasBabyInfo = babyName || babyBirthdate;
    const topOffset = hasBabyInfo ? 80 : 0;
    const centerY = hasBabyInfo ? c * 0.85 : c;
    
    if (i === 0) return { x: c, y: centerY + topOffset };
    
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const angle = i * goldenAngle;
    const radius = Math.sqrt(i) * 75;
    
    const seed = i * 12.9898;
    const randomAngle = (Math.sin(seed) * 0.5) * Math.PI;
    const randomRadius = Math.cos(seed * 1.618) * 30;
    
    return {
      x: c + Math.cos(angle + randomAngle) * (radius + randomRadius),
      y: centerY + topOffset + Math.sin(angle + randomAngle) * (radius + randomRadius)
    };
  };

  const SimpleFlower = ({ w }) => {
    const { cs, text, order } = w;
    // Handle both UUID strings from database and timestamp numbers from localStorage
    const numericId = typeof w.id === 'string' ? parseInt(w.id.replace(/\D/g, '').slice(0, 10)) : w.id;
    const seed = numericId % 1000;
    const r1 = Math.sin(seed * 0.1) * 0.5 + 0.5;

    // Handle both old format (p/c/l) and new format (petals/center/leaf)
    const petals = cs?.petals || cs?.p;
    const center = cs?.center || cs?.c;
    const leaf = cs?.leaf || cs?.l;

    // Safety check - if colors are missing, don't render
    if (!petals || !center || !leaf) {
      console.error('Missing flower colors:', cs);
      return null;
    }

    return (
      <>
        <path d={`M 0 100 Q ${(r1-0.5)*25} 70, 0 0`} stroke={stemColor} strokeWidth="2.5" fill="none" opacity="0.85"/>
        <path d="M -8 40 Q -22 38, -18 48 Q -15 50, -6 44 Z" fill={leaf} opacity="0.75"/>
        
        {[0,1,2,3,4,5].map(j => {
          const a = (j/6)*Math.PI*2;
          return <g key={j} transform={`rotate(${a*180/Math.PI})`}>
            <path d="M 0 0 Q 2 -25, 0 -35 Q 10 -32, 9 -18 Q 4 0, 0 0 Q -4 0, -9 -18 Q -10 -32, 0 -35 Z"
              fill={petals[j%3]} opacity="0.9"/>
          </g>;
        })}
        <circle cx="0" cy="0" r="13" fill={center} opacity="0.9"/>
        
        {order === 0 ? (
          <>
            <text x="6" y="58" fill="#6B5B4F" fontSize="8" 
              fontFamily="Georgia" fontStyle="italic" opacity="0.65">{text}</text>
            <text x="6" y="67" fill="#6B5B4F" fontSize="6.5" 
              fontFamily="Georgia" opacity="0.5">№ 1</text>
          </>
        ) : (
          <text x="6" y="62" fill="#6B5B4F" fontSize="7.5" 
            fontFamily="Georgia" fontStyle="italic" opacity="0.6">{order + 1}. {text}</text>
        )}
      </>
    );
  };

  const Butterfly = ({ x, y, size = 1 }) => {
    return (
      <g transform={`translate(${x}, ${y}) scale(${size})`}>
        <ellipse cx="-8" cy="0" rx="10" ry="15" fill="#E8B864" opacity="0.7" transform="rotate(-20, -8, 0)"/>
        <ellipse cx="8" cy="0" rx="10" ry="15" fill="#E8B864" opacity="0.7" transform="rotate(20, 8, 0)"/>
        <ellipse cx="-8" cy="5" rx="7" ry="10" fill="#D19B4A" opacity="0.8" transform="rotate(-20, -8, 5)"/>
        <ellipse cx="8" cy="5" rx="7" ry="10" fill="#D19B4A" opacity="0.8" transform="rotate(20, 8, 5)"/>
        <line x1="0" y1="-5" x2="0" y2="15" stroke="#5B4A42" strokeWidth="1.5"/>
        <circle cx="-2" cy="-6" r="1.5" fill="#5B4A42"/>
        <circle cx="2" cy="-6" r="1.5" fill="#5B4A42"/>
      </g>
    );
  };

  const Bee = ({ x, y, size = 1 }) => (
    <g transform={`translate(${x}, ${y}) scale(${size * 1.4})`}>
      <ellipse cx="-9" cy="-2" rx="6" ry="3.5" fill="#FFF" opacity="0.75" stroke="#3D3D3D" strokeWidth="0.8" transform="rotate(-25, -9, -2)"/>
      <ellipse cx="9" cy="-2" rx="6" ry="3.5" fill="#FFF" opacity="0.75" stroke="#3D3D3D" strokeWidth="0.8" transform="rotate(25, 9, -2)"/>
      <ellipse cx="0" cy="0" rx="7" ry="5.5" fill="#FFD700" opacity="0.95"/>
      <ellipse cx="-2.5" cy="0" rx="1.5" ry="5" fill="#3D3D3D" opacity="0.8"/>
      <ellipse cx="2" cy="0" rx="1.5" ry="5" fill="#3D3D3D" opacity="0.8"/>
      <circle cx="0" cy="-6" r="3" fill="#3D3D3D" opacity="0.9"/>
      <line x1="-1.5" y1="-8" x2="-2" y2="-10" stroke="#3D3D3D" strokeWidth="0.8"/>
      <line x1="1.5" y1="-8" x2="2" y2="-10" stroke="#3D3D3D" strokeWidth="0.8"/>
      <circle cx="-2" cy="-10" r="0.8" fill="#3D3D3D"/>
      <circle cx="2" cy="-10" r="0.8" fill="#3D3D3D"/>
    </g>
  );

  const Ladybug = ({ x, y, size = 1 }) => (
    <g transform={`translate(${x}, ${y}) scale(${size})`}>
      <circle cx="0" cy="0" r="5" fill="#D4696B" opacity="0.9"/>
      <line x1="0" y1="-5" x2="0" y2="5" stroke="#5B4A42" strokeWidth="0.8"/>
      <circle cx="-2" cy="-2" r="1.2" fill="#5B4A42" opacity="0.8"/>
      <circle cx="2" cy="-2" r="1.2" fill="#5B4A42" opacity="0.8"/>
      <circle cx="-2" cy="2" r="1.2" fill="#5B4A42" opacity="0.8"/>
      <circle cx="2" cy="2" r="1.2" fill="#5B4A42" opacity="0.8"/>
      <circle cx="0" cy="6" r="3" fill="#5B4A42" opacity="0.9"/>
    </g>
  );

  const Flower = ({ w, i }) => {
    const pos = getPos(i);
    const { rot, sc, order } = w;
    
    let scale;
    if (order === 0) {
      const n = words.length;
      if (n <= 20) {
        scale = sc;
      } else {
        const baseScale = getScale();
        scale = sc * Math.max(0.6, 1 - (n - 20) * 0.01) * (baseScale / 1.0) * 1.3;
      }
    } else {
      scale = sc * getScale();
    }

    return (
      <g transform={`translate(${pos.x}, ${pos.y}) rotate(${rot}) scale(${scale})`}>
        <SimpleFlower w={w} />
      </g>
    );
  };

  const MilestoneDecorations = () => {
    const vb = getVB();
    const decorations = [];
    
    if (words.length >= 10) decorations.push(<Butterfly key="b10" x={vb * (0.65 + insectSeed * 0.2)} y={vb * (0.15 + insectSeed * 0.1)} size={1.2} />);
    if (words.length >= 25) decorations.push(<Butterfly key="b25" x={vb * (0.15 + insectSeed * 0.15)} y={vb * (0.7 + insectSeed * 0.1)} size={1.1} />);
    if (words.length >= 50) decorations.push(<Butterfly key="b50" x={vb * (0.75 + insectSeed * 0.1)} y={vb * (0.75 + insectSeed * 0.1)} size={1.3} />);
    if (words.length >= 5) decorations.push(<Bee key="bee1" x={vb * (0.25 + insectSeed * 0.15)} y={vb * (0.2 + insectSeed * 0.1)} size={0.9} />);
    if (words.length >= 15) {
      decorations.push(<Ladybug key="lb1" x={vb * (0.65 + insectSeed * 0.15)} y={vb * (0.55 + insectSeed * 0.15)} size={1.0} />);
      decorations.push(<Bee key="bee2" x={vb * (0.55 + insectSeed * 0.15)} y={vb * (0.25 + insectSeed * 0.1)} size={0.8} />);
    }
    if (words.length >= 30) decorations.push(<Ladybug key="lb2" x={vb * (0.3 + insectSeed * 0.15)} y={vb * (0.6 + insectSeed * 0.15)} size={0.9} />);
    
    return decorations;
  };

  const exp = (width, height, sizeName, format = 'png') => {
    const s = gardenRef.current;
    
    const svgClone = s.cloneNode(true);
    
    const viewBox = svgClone.getAttribute('viewBox').split(' ').map(Number);
    const svgWidth = viewBox[2] - viewBox[0];
    const svgHeight = viewBox[3] - viewBox[1];
    
    const targetSize = Math.min(width, height);
    const scaleFactor = targetSize / Math.min(svgWidth, svgHeight);
    
    svgClone.setAttribute('width', svgWidth * scaleFactor);
    svgClone.setAttribute('height', svgHeight * scaleFactor);
    
    const c = document.createElement('canvas');
    const ctx = c.getContext('2d');
    const img = new Image();
    
    c.width = width;
    c.height = height;
    
    img.onload = () => {
      ctx.fillStyle = currentBg;
      ctx.fillRect(0, 0, width, height);
      
      const svgAspect = 1;
      const canvasAspect = width / height;
      
      let drawWidth, drawHeight, offsetX, offsetY;
      
      if (canvasAspect > svgAspect) {
        drawHeight = height;
        drawWidth = height * svgAspect;
        offsetX = (width - drawWidth) / 2;
        offsetY = 0;
      } else {
        drawWidth = width;
        drawHeight = width / svgAspect;
        offsetX = 0;
        offsetY = (height - drawHeight) / 2;
      }
      
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
      const l = document.createElement('a');
      const extension = format === 'jpeg' ? 'jpg' : 'png';
      const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
      l.download = `word-bloom-${words.length}-words-${sizeName}.${extension}`;
      l.href = c.toDataURL(mimeType, 0.95);
      l.click();
      setShowPrintMenu(false);
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(new XMLSerializer().serializeToString(svgClone))));
  };

  const handleDeleteWord = async (wordId) => {
    if (user && garden) {
      // Logged in - delete from database
      try {
        await deleteWord(wordId);
        setWords(words.filter(w => w.id !== wordId));
      } catch (error) {
        console.error('Error deleting word:', error);
        alert('Failed to delete word. Please try again.');
      }
    } else {
      // Not logged in - just remove from local state
      setWords(words.filter(w => w.id !== wordId));
    }
  };

  const exportData = () => {
    // Create CSV content
    const csvContent = [
      ['Word', 'Date', 'Order'].join(','),
      ...words
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map(w => [
          `"${w.text}"`,
          w.date,
          w.order + 1
        ].join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `word-bloom-data-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadClick = () => {
    if (!user) {
      setShowLoginPrompt(true);
    } else {
      setShowPrintMenu(!showPrintMenu);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#FFF9F0' }}>
        <p style={{ color: '#8B7355', fontSize: '18px' }}>Loading your garden...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Hide mobile user info on desktop */
        .mobile-user-info {
          display: none;
        }
        
        /* Mobile responsive styles */
        @media (max-width: 768px) {
          .word-bloom-container {
            padding: 12px !important;
          }
          
          .header-layout {
            flex-direction: column !important;
            gap: 12px !important;
          }
          
          .header-layout > div:first-child,
          .header-layout > div:last-child {
            display: none !important;
          }
          
          .mobile-user-info {
            display: block !important;
          }
          
          input[type="date"] {
            max-width: 100% !important;
          }
          
          .word-bloom-title {
            font-size: 36px !important;
            letter-spacing: 1px !important;
          }
          
          .beta-badge {
            font-size: 10px !important;
            padding: 3px 6px !important;
          }
          
          .user-info {
            order: -1 !important;
            align-self: center !important;
          }
          
          .action-buttons {
            flex-direction: column !important;
            width: 100% !important;
            gap: 12px !important;
          }
          
          .action-buttons button {
            width: 100% !important;
            justify-content: center !important;
          }
          
          .garden-container {
            padding: 12px !important;
            margin-bottom: 16px !important;
          }
          
          .garden-canvas {
            max-width: 100% !important;
            overflow-x: auto !important;
          }
          
          .garden-controls {
            flex-wrap: wrap !important;
            justify-content: space-between !important;
            gap: 12px !important;
          }
          
          .garden-controls > button:first-child,
          .garden-controls > div:last-child {
            flex: 1 1 45% !important;
          }
          
          .modal-content {
            margin: 20px !important;
            padding: 24px !important;
            max-height: 85vh !important;
            overflow-y: auto !important;
          }
          
          .download-menu {
            width: calc(100% - 40px) !important;
            max-width: none !important;
          }
        }
      `}</style>
    <div style={{ fontFamily: 'Georgia', backgroundColor: '#FFF9F0', minHeight: '100vh', padding: '20px' }} className="word-bloom-container">
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }} className="header-layout">
          <div style={{ flex: 1 }}></div>
          <h1 style={{ fontSize: '56px', color: '#5B4A42', textAlign: 'center', fontWeight: '300', letterSpacing: '2px', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }} className="word-bloom-title">
            Word Bloom
            <span style={{ fontSize: '12px', color: '#A89584', fontWeight: '500', letterSpacing: '1px', backgroundColor: '#F5E6D3', padding: '4px 8px', borderRadius: '4px', textTransform: 'uppercase' }} className="beta-badge">Beta</span>
          </h1>
          <div style={{ flex: 1, textAlign: 'right' }}>
            {user && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }} className="user-info">
                <span style={{ fontSize: '12px', color: '#8B7355' }}>{user.email}</span>
                <button 
                  onClick={async () => {
                    const { signOut } = await import('./lib/api');
                    await signOut();
                    window.location.reload();
                  }}
                  style={{ 
                    backgroundColor: 'transparent', 
                    border: '1px solid #E8DCC8', 
                    color: '#8B7355', 
                    padding: '6px 12px', 
                    borderRadius: '6px', 
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
        <p style={{ fontSize: '19px', color: '#8B7355', fontStyle: 'italic', textAlign: 'center', marginBottom: '40px' }}>
          A garden of first words
        </p>

        {!user && words.length >= 7 && words.length < 10 && (
          <div style={{ 
            backgroundColor: '#FFF9F0', 
            border: '1px solid #E8DCC8', 
            padding: '12px 20px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            textAlign: 'center',
            maxWidth: '500px',
            margin: '0 auto 20px'
          }}>
            <p style={{ color: '#8B7355', fontSize: '14px', margin: 0 }}>
              💡 {10 - words.length} more {10 - words.length === 1 ? 'word' : 'words'} left before you'll need to sign in to continue
            </p>
          </div>
        )}

        <div style={{ display: 'flex', gap: '16px', marginBottom: '30px', justifyContent: 'center', flexWrap: 'wrap' }} className="action-buttons">
          <button onClick={() => setShowAddForm(!showAddForm)} 
            style={{ backgroundColor: '#7A9B6F', color: 'white', border: 'none', padding: '14px 28px', 
              borderRadius: '8px', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Plus size={20} />plant a word
          </button>
          
          {words.length > 0 && (
            <button onClick={() => setShowSettings(!showSettings)} 
              style={{ backgroundColor: '#8B7355', color: 'white', border: 'none', padding: '14px 28px', 
                borderRadius: '8px', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={20} />baby's name
            </button>
          )}
        </div>

        {showLoginPrompt && !user && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{ 
              backgroundColor: 'white', 
              padding: '40px', 
              borderRadius: '12px', 
              maxWidth: '450px',
              border: '2px solid #E8DCC8',
              position: 'relative'
            }}>
              <h2 style={{ color: '#5B4A42', marginTop: 0, marginBottom: '16px' }}>Save your garden</h2>
              <p style={{ color: '#8B7355', marginBottom: '24px', fontSize: '15px' }}>
                Create a free account to download your garden and access it from any device.
              </p>
              
              {/* Inline Login Form */}
              <form onSubmit={async (e) => {
                e.preventDefault();
                const email = e.target.email.value;
                const marketingConsent = e.target.marketing.checked;
                
                try {
                  const { signInWithEmail } = await import('./lib/api');
                  await signInWithEmail(email, marketingConsent);
                  alert('Check your email for a magic link!');
                  setShowLoginPrompt(false);
                } catch (error) {
                  console.error('Login error:', error);
                  alert('Something went wrong. Please try again.');
                }
              }}>
                <input 
                  type="email" 
                  name="email"
                  placeholder="your@email.com" 
                  required
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    border: '2px solid #E8DCC8', 
                    fontSize: '16px',
                    marginBottom: '16px',
                    boxSizing: 'border-box'
                  }} 
                />
                
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '8px', 
                  marginBottom: '16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#5B4A42'
                }}>
                  <input 
                    type="checkbox" 
                    name="marketing"
                    defaultChecked={true}
                    style={{ 
                      marginTop: '2px',
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer'
                    }} 
                  />
                  <span>
                    Keep me updated about Word Bloom (
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPrivacy(true);
                      }}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: '#7A9B6F', 
                        textDecoration: 'underline', 
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: 'inherit'
                      }}>
                      Privacy Policy
                    </button>
                    )
                  </span>
                </label>
                
                <button 
                  type="submit"
                  style={{ 
                    width: '100%',
                    backgroundColor: '#7A9B6F', 
                    color: 'white', 
                    border: 'none', 
                    padding: '14px', 
                    borderRadius: '8px', 
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                  Send magic link
                </button>
              </form>
              
              <button 
                onClick={() => setShowLoginPrompt(false)}
                style={{ 
                  marginTop: '16px',
                  width: '100%',
                  backgroundColor: 'transparent', 
                  color: '#8B7355', 
                  border: '2px solid #E8DCC8', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  fontSize: '15px'
                }}>
                Maybe later
              </button>
            </div>
          </div>
        )}

        {showSettings && (
          <div style={{ backgroundColor: 'white', padding: '28px', borderRadius: '12px', marginBottom: '30px', 
            maxWidth: '500px', margin: '0 auto 30px', border: '2px solid #E8DCC8' }}>
            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#5B4A42', fontWeight: '500' }}>Baby's First Name</label>
              <input type="text" value={babyName} onChange={(e) => setBabyName(e.target.value)} 
                placeholder="Optional"
                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '2px solid #E8DCC8', fontSize: '16px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#5B4A42', fontWeight: '500' }}>Birthdate</label>
              <input type="date" value={babyBirthdate} onChange={(e) => setBabyBirthdate(e.target.value)}
                style={{ width: 'calc(100% - 28px)', padding: '12px', borderRadius: '6px', border: '2px solid #E8DCC8', fontSize: '16px' }} />
            </div>
            <button onClick={() => setShowSettings(false)} 
              style={{ backgroundColor: '#7A9B6F', color: 'white', border: 'none', padding: '14px 24px', 
                borderRadius: '8px', cursor: 'pointer', fontSize: '16px', width: '100%', fontWeight: '500' }}>
              done
            </button>
          </div>
        )}

        {showAddForm && (
          <div style={{ backgroundColor: 'white', padding: '28px', borderRadius: '12px', marginBottom: '30px', 
            maxWidth: '500px', margin: '0 auto 30px', border: '2px solid #E8DCC8' }}>
            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#5B4A42', fontWeight: '500' }}>Word</label>
              <input type="text" value={newWord} onChange={(e) => setNewWord(e.target.value)} 
                placeholder="mama, dog, ball..." onKeyPress={(e) => e.key === 'Enter' && addWord()}
                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '2px solid #E8DCC8', fontSize: '16px', boxSizing: 'border-box' }} autoFocus />
            </div>
            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#5B4A42', fontWeight: '500' }}>Date</label>
              <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                style={{ width: 'calc(100% - 28px)', padding: '12px', borderRadius: '6px', border: '2px solid #E8DCC8', fontSize: '16px' }} />
            </div>
            <button onClick={addWord} disabled={!newWord.trim()} 
              style={{ backgroundColor: newWord.trim() ? '#7A9B6F' : '#ccc', color: 'white', border: 'none', 
                padding: '14px 24px', borderRadius: '8px', cursor: newWord.trim() ? 'pointer' : 'not-allowed', 
                fontSize: '16px', width: '100%', fontWeight: '500' }}>
              add to garden
            </button>
          </div>
        )}

        {words.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '100px 20px', color: '#A89584' }}>
            <p style={{ fontSize: '22px', fontStyle: 'italic', marginBottom: '12px' }}>Your garden awaits...</p>
            <p style={{ fontSize: '17px', marginBottom: '32px' }}>Plant your child's first word and watch it bloom</p>
            {!user && (
              <button
                onClick={() => setShowLoginPrompt(true)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#8B7355',
                  fontSize: '14px',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                Returning user? Sign in to access your saved garden
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '30px', border: '3px solid #E8DCC8', marginBottom: '20px' }} className="garden-container">
              <p style={{ color: '#8B7355', fontSize: '15px', fontStyle: 'italic', marginBottom: '20px' }}>
                {words.length} word{words.length !== 1 ? 's' : ''} blooming
              </p>
              <svg ref={gardenRef} viewBox={`-20 -20 ${getVB() + 40} ${getVB() + 40}`} 
                style={{ width: '100%', height: 'auto', backgroundColor: currentBg, borderRadius: '8px' }}>
                {words.filter(w => w.order !== 0).map((w, i) => <Flower key={w.id} w={w} i={words.findIndex(word => word.id === w.id)} />)}
                <MilestoneDecorations />
                {words.filter(w => w.order === 0).map((w, i) => <Flower key={w.id} w={w} i={0} />)}
                
                {(babyName || babyBirthdate) && (
                  <g>
                    {babyName && (
                      <text 
                        x={getVB() / 2} 
                        y="30" 
                        textAnchor="middle" 
                        fill={bgIndex === 2 ? '#E8DCC8' : '#5B4A42'} 
                        fontSize="24" 
                        fontFamily="Georgia" 
                        fontStyle="italic"
                        opacity="0.85"
                      >
                        {babyName}
                      </text>
                    )}
                    {babyBirthdate && (
                      <text 
                        x={getVB() / 2} 
                        y={babyName ? "50" : "35"} 
                        textAnchor="middle" 
                        fill={bgIndex === 2 ? '#C4B5A8' : '#8B7355'} 
                        fontSize="14" 
                        fontFamily="Georgia"
                        opacity="0.75"
                      >
                        Born {new Date(babyBirthdate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </text>
                    )}
                  </g>
                )}
              </svg>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }} className="garden-controls">
              {words.length > 0 && (
                <button onClick={rearrange} 
                  style={{ backgroundColor: '#7A9B6F', color: 'white', border: 'none', padding: '12px 24px', 
                    borderRadius: '8px', cursor: 'pointer', fontSize: '15px' }}>
                  rearrange
                </button>
              )}
              
              {/* Background selector - cycles through options */}
              <button 
                onClick={() => setBgIndex((bgIndex + 1) % bgOptions.length)}
                style={{ 
                  backgroundColor: '#8B7355', 
                  color: 'white', 
                  border: 'none', 
                  padding: '12px 24px', 
                  borderRadius: '8px', 
                  cursor: 'pointer', 
                  fontSize: '15px'
                }}>
                new background
              </button>
              
              {words.length > 0 && (
                <div style={{ position: 'relative' }}>
                    <button onClick={handleDownloadClick} 
                      style={{ backgroundColor: '#B87B8E', color: 'white', border: 'none', padding: '12px 24px', 
                        borderRadius: '8px', cursor: 'pointer', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Download size={18} />download
                    </button>
                    
                    {showPrintMenu && user && (
                      <div style={{ 
                        position: 'absolute', 
                        top: '100%', 
                        left: '0', 
                        marginTop: '8px',
                        backgroundColor: 'white', 
                        border: '2px solid #E8DCC8', 
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        zIndex: 10,
                        minWidth: '200px'
                      }} className="download-menu">
                        <button 
                          onClick={() => exp(2400, 3000, '8x10')}
                          style={{ 
                            width: '100%', 
                            padding: '12px 16px', 
                            border: 'none', 
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#5B4A42',
                            borderBottom: '1px solid #F5E6D3'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#FFF9F0'}
                          onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <strong>8×10"</strong> (2400×3000px)<br/>
                          <span style={{ fontSize: '12px', color: '#8B7355' }}>Standard frame</span>
                        </button>
                        
                        <button 
                          onClick={() => exp(3300, 4200, '11x14')}
                          style={{ 
                            width: '100%', 
                            padding: '12px 16px', 
                            border: 'none', 
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#5B4A42',
                            borderBottom: '1px solid #F5E6D3'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#FFF9F0'}
                          onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <strong>11×14"</strong> (3300×4200px)<br/>
                          <span style={{ fontSize: '12px', color: '#8B7355' }}>Medium print</span>
                        </button>
                        
                        <button 
                          onClick={() => exp(4800, 6000, '16x20')}
                          style={{ 
                            width: '100%', 
                            padding: '12px 16px', 
                            border: 'none', 
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#5B4A42'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#FFF9F0'}
                          onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <strong>16×20"</strong> (4800×6000px)<br/>
                          <span style={{ fontSize: '12px', color: '#8B7355' }}>Large poster</span>
                        </button>
                      </div>
                    )}
                  </div>
              )}
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', border: '2px solid #E8DCC8' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, color: '#5B4A42', fontSize: '18px' }}>Word List</h3>
                {words.length > 0 && (
                  <button 
                    onClick={exportData}
                    style={{ 
                      backgroundColor: 'transparent',
                      border: '1px solid #E8DCC8',
                      color: '#8B7355',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}>
                    Export Data
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gap: '10px' }}>
                {words.map((w) => (
                  <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                    padding: '12px', backgroundColor: '#FFF9F0', borderRadius: '6px' }}>
                    <div>
                      <strong style={{ color: '#4A4A4A', fontSize: '15px' }}>{w.text}</strong>
                      {w.order === 0 && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#7A9B6F' }}>✨ First word</span>}
                      <span style={{ color: '#999', marginLeft: '12px', fontSize: '14px' }}>
                        {new Date(w.date).toLocaleDateString()}
                      </span>
                    </div>
                    <button onClick={() => handleDeleteWord(w.id)}
                      style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', 
                        color: '#D4696B', padding: '4px' }}>
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Footer */}
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px 20px', 
          color: '#8B7355', 
          fontSize: '14px',
          borderTop: '1px solid #E8DCC8',
          marginTop: '40px'
        }}>
          {user && (
            <div style={{ marginBottom: '16px' }} className="mobile-user-info">
              <div style={{ fontSize: '12px', marginBottom: '8px' }}>{user.email}</div>
              <button 
                onClick={async () => {
                  const { signOut } = await import('./lib/api');
                  await signOut();
                  window.location.reload();
                }}
                style={{ 
                  backgroundColor: 'transparent', 
                  border: '1px solid #E8DCC8', 
                  color: '#8B7355', 
                  padding: '6px 12px', 
                  borderRadius: '6px', 
                  cursor: 'pointer',
                  fontSize: '13px'
                }}>
                Sign out
              </button>
            </div>
          )}
          <div>
            <button onClick={() => setShowPrivacy(true)} style={{ background: 'none', border: 'none', color: '#8B7355', cursor: 'pointer', textDecoration: 'underline', padding: '0 10px' }}>Privacy</button>
            <span>•</span>
            <button onClick={() => setShowTerms(true)} style={{ background: 'none', border: 'none', color: '#8B7355', cursor: 'pointer', textDecoration: 'underline', padding: '0 10px' }}>Terms</button>
            <span>•</span>
            <button onClick={() => setShowAbout(true)} style={{ background: 'none', border: 'none', color: '#8B7355', cursor: 'pointer', textDecoration: 'underline', padding: '0 10px' }}>About</button>
            {user && (
              <>
                <span>•</span>
                <button 
                  onClick={() => {
                    alert('To delete your account and all data, please email hello@wordbloom.app with your email address. We\'ll process deletion requests within 30 days.');
                  }}
                  style={{ background: 'none', border: 'none', color: '#C4A89F', cursor: 'pointer', textDecoration: 'underline', padding: '0 10px', fontSize: '12px' }}>
                  Delete account
                </button>
              </>
            )}
          </div>
        </div>

        {/* Privacy Modal */}
        {showPrivacy && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
            <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '12px', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto', border: '2px solid #E8DCC8' }} className="modal-content">
              <h2 style={{ color: '#5B4A42', marginTop: 0 }}>Privacy Policy</h2>
              <p style={{ fontSize: '14px', color: '#8B7355' }}>Last updated: February 2, 2026</p>
              <p style={{ color: '#5B4A42' }}>Word Bloom is a simple tool to track and visualize your child's first words. We take your privacy seriously.</p>
              
              <h3 style={{ color: '#5B4A42', fontSize: '16px' }}>What we collect:</h3>
              <ul style={{ color: '#5B4A42' }}>
                <li>Your email address (for login)</li>
                <li>Your child's words and the dates you enter</li>
                <li>Your child's first name and birthdate (if you choose to add them)</li>
              </ul>
              
              <h3 style={{ color: '#5B4A42', fontSize: '16px' }}>How we use it:</h3>
              <ul style={{ color: '#5B4A42' }}>
                <li>To provide the service (store your garden, send login links)</li>
                <li>We never sell or share your data with third parties</li>
                <li>We don't use cookies or tracking</li>
              </ul>
              
              <h3 style={{ color: '#5B4A42', fontSize: '16px' }}>Where it's stored:</h3>
              <ul style={{ color: '#5B4A42' }}>
                <li>All data is stored securely with Supabase (a trusted database provider)</li>
                <li>Your data is encrypted and private - only you can see it</li>
              </ul>
              
              <h3 style={{ color: '#5B4A42', fontSize: '16px' }}>Your rights:</h3>
              <ul style={{ color: '#5B4A42' }}>
                <li>You can export your data anytime using the "Export Data" button</li>
                <li>You can delete your account and data by contacting us</li>
              </ul>
              
              <p style={{ color: '#5B4A42', marginTop: '20px' }}><strong>Contact:</strong> hello@wordbloom.app</p>
              
              <button onClick={() => setShowPrivacy(false)} style={{ marginTop: '20px', backgroundColor: '#7A9B6F', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        )}

        {/* Terms Modal */}
        {showTerms && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
            <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '12px', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto', border: '2px solid #E8DCC8' }} className="modal-content">
              <h2 style={{ color: '#5B4A42', marginTop: 0 }}>Terms of Service</h2>
              <p style={{ fontSize: '14px', color: '#8B7355' }}>Last updated: February 2, 2026</p>
              <p style={{ color: '#5B4A42' }}>By using Word Bloom, you agree to these terms.</p>
              
              <h3 style={{ color: '#5B4A42', fontSize: '16px' }}>The Service:</h3>
              <ul style={{ color: '#5B4A42' }}>
                <li>Word Bloom is provided "as is" for tracking your child's first words</li>
                <li>Currently free during beta testing</li>
                <li>We may introduce paid features in the future</li>
              </ul>
              
              <h3 style={{ color: '#5B4A42', fontSize: '16px' }}>Your Account:</h3>
              <ul style={{ color: '#5B4A42' }}>
                <li>You're responsible for keeping your email secure</li>
                <li>One account per person</li>
                <li>Don't use the service for anything harmful or illegal</li>
              </ul>
              
              <h3 style={{ color: '#5B4A42', fontSize: '16px' }}>Your Content:</h3>
              <ul style={{ color: '#5B4A42' }}>
                <li>You own all the words and data you enter</li>
                <li>We don't claim any rights to your content</li>
                <li>You can export or delete your data anytime</li>
              </ul>
              
              <h3 style={{ color: '#5B4A42', fontSize: '16px' }}>Liability:</h3>
              <ul style={{ color: '#5B4A42' }}>
                <li>We're not responsible if something goes wrong (it's a free beta!)</li>
                <li>Use at your own risk</li>
              </ul>
              
              <h3 style={{ color: '#5B4A42', fontSize: '16px' }}>Changes:</h3>
              <ul style={{ color: '#5B4A42' }}>
                <li>We may update these terms - we'll email you if we make major changes</li>
              </ul>
              
              <p style={{ color: '#5B4A42', marginTop: '20px' }}><strong>Questions:</strong> hello@wordbloom.app</p>
              
              <button onClick={() => setShowTerms(false)} style={{ marginTop: '20px', backgroundColor: '#7A9B6F', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        )}

        {/* About Modal */}
        {showAbout && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
            <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '12px', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto', border: '2px solid #E8DCC8' }} className="modal-content">
              <h2 style={{ color: '#5B4A42', marginTop: 0 }}>About Word Bloom</h2>
              <p style={{ color: '#5B4A42', lineHeight: '1.6' }}>Word Bloom helps parents track and celebrate their child's first words.</p>
              <p style={{ color: '#5B4A42', lineHeight: '1.6' }}>Every new word your child learns is a milestone worth remembering. Word Bloom turns those moments into a beautiful visual garden you can print and frame.</p>
              
              <h3 style={{ color: '#5B4A42', fontSize: '16px' }}>How it works:</h3>
              <ol style={{ color: '#5B4A42', lineHeight: '1.6' }}>
                <li>Add your child's words as they learn them</li>
                <li>Watch them bloom into a unique garden</li>
                <li>Download high-resolution prints for framing</li>
              </ol>
              
              <p style={{ color: '#5B4A42', lineHeight: '1.6' }}>Built by a parent who kept losing track of words in notes apps.</p>
              
              <p style={{ color: '#5B4A42', marginTop: '20px' }}><strong>Contact:</strong> hello@wordbloom.app</p>
              <p style={{ color: '#8B7355', fontSize: '14px' }}>Questions, feedback, or just want to say hi? We'd love to hear from you.</p>
              
              <button onClick={() => setShowAbout(false)} style={{ marginTop: '20px', backgroundColor: '#7A9B6F', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <WordBloomApp />
    </AuthProvider>
  );
}

export default App;